import { Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { useAuth } from './context/AuthContext';
import theme from './theme';

// Import components directly
import Login from './pages/Login';
import SuperAdminRegister from './pages/InitialAdminRegister';
import TwoFactorLogin from './pages/TwoFactorLogin';
import IntermediateFeature from './components/IntermediateFeature';
import ResetPassword from './pages/ResetPassword';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
      retry: 1
    }
  }
});

const PublicRoute = ({ children }) => {
  const { isAuthenticated, isFullyAuthenticated } = useAuth();
  const username = sessionStorage.getItem('username');
  const tempPassword = sessionStorage.getItem('tempPassword');
  const currentPath = window.location.pathname;
  
  // Allow access to 2FA login page even if username is stored
  if (currentPath === '/2fa-login' && username) {
    return children;
  }
  
  // Allow access to 2FA setup page if credentials are stored
  if (currentPath === '/2fa-setup' && username && tempPassword) {
    return children;
  }
  
  // Allow access to login page always (for logout scenarios)
  if (currentPath === '/login') {
    return children;
  }
  
  // Allow access to super admin register page always
  if (currentPath === '/super-admin-register') {
    return children;
  }
  
  // If user is fully authenticated, redirect to dashboard instead of landing
  if (isFullyAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  // For other routes, only allow if not authenticated
  return !isAuthenticated ? children : <Navigate to="/dashboard" replace />;
};

const LoginApp = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Routes>
          {/* Login route as default */}
          <Route path="/" element={
            <PublicRoute><Login /></PublicRoute>
          } />
          
          {/* Login route */}
          <Route path="/login" element={
            <PublicRoute><Login /></PublicRoute>
          } />

          {/* Super admin register route */}
          <Route path="/super-admin-register" element={
            <PublicRoute><SuperAdminRegister /></PublicRoute>
          } />

          {/* Two factor login route */}
          <Route path="/2fa-login" element={
            <PublicRoute><TwoFactorLogin /></PublicRoute>
          } />

          {/* Two factor setup route */}
          <Route path="/2fa-setup" element={
            <PublicRoute><IntermediateFeature /></PublicRoute>
          } />

          {/* Reset password route */}
          <Route path="/reset-password" element={
            <PublicRoute><ResetPassword /></PublicRoute>
          } />

          {/* Catch all other routes and redirect to login */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default LoginApp; 